// A station's session's server state (spec/13 §3.3, F-SES-14/-16/-17/-20/-21), as TanStack Query hooks
// over the generated client — `packages/contracts/src/routes/sessions.ts` — for the Floor's tiles, List view
// and station modal:
//
//   GET  …/venues/{v}/stations/{s}/session   the station's live session, if any   sessions:read
//   GET  …/sessions/{id}                      status, time left, the plan          sessions:read
//   GET  …/sessions/{id}/timeline             segments and the event history       sessions:read
//   GET  …/sessions/{id}/ledger               journal entries, newest first, paged  sessions:read
//   POST …/sessions/{id}/comp                 add free time (a comp)               sessions:comp
//   POST …/sessions/{id}/sell-time            sell paid minutes                    orders:create
//   POST …/sessions/{id}/message              message the player                   stations:message
//   POST …/sessions/{id}/end                  end the session                      sessions:end
//   POST …/sessions                           start a session on a free station    sessions:start
//
// The drawer opens from a station tile (`getStationSession`); a session action re-reads the session
// and its timeline so the drawer stays live. A station's session is also kept current by the live
// floor push (`realtime.ts`, `useSessionsLive`): a `floor.station.updated` re-reads that station's
// session, so the list and the drawer's free/in-session state follow it. When the socket is not live
// the station-session lookup polls on the interval below (spec/09 §8 fallback).

import type {
  AddSessionTime,
  CompSession,
  ConsoleQuote,
  EndSession,
  FloorStationState,
  MessageSession,
  MoveSession,
  QuoteConsoleSession,
  QuoteSessionChange,
  SellSessionTime,
  Session,
  SessionChangeQuote,
  SessionEvent,
  SessionLedgerPage,
  SessionMoveResult,
  SessionTimeline,
  SetSessionControllers,
  StartSession,
} from '@clavian/api-client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';
import { PROBLEM, problemCode } from '../../api/problems';
import { isOccupied, isStaffRun } from '../../floor/live';
import { floorKeys } from '../../floor/queries';
import { useRealtimeLive } from '../../realtime';

/** How often a station's session is re-read while the drawer or list is open and the push is not carrying it. */
const STATION_SESSION_REFRESH_MS = 10_000;

export const sessionKeys = {
  onStation: (organizationId: string, venueId: string, stationId: string) =>
    ['session', 'on-station', organizationId, venueId, stationId] as const,
  /** The prefix every station-session read of a venue shares, to resync them all at once (reconnect, spec/09 §4). */
  onStationAll: (organizationId: string, venueId: string) => ['session', 'on-station', organizationId, venueId] as const,
  one: (organizationId: string, sessionId: string) => ['session', 'one', organizationId, sessionId] as const,
  timeline: (organizationId: string, sessionId: string) => ['session', 'timeline', organizationId, sessionId] as const,
  /** Under the timeline's key, so whatever re-reads the timeline after an action re-reads the ledger too. */
  ledger: (organizationId: string, sessionId: string) => ['session', 'timeline', organizationId, sessionId, 'ledger'] as const,
};

/** Journal entries per ledger page. The drawer shows ten rows at a time; a page holds enough to group them. */
export const SESSION_LEDGER_PAGE_SIZE = 50;

/** The session running on a station, or `null` when the station is free (the endpoint's 404). */
export function useStationSession(organizationId: string, venueId: string, stationId: string, enabled = true) {
  const api = useApi();
  const live = useRealtimeLive();
  return useQuery<Session | null>({
    queryKey: sessionKeys.onStation(organizationId, venueId, stationId),
    enabled,
    refetchInterval: live ? false : STATION_SESSION_REFRESH_MS,
    queryFn: async () => {
      try {
        return await api.getStationSession({ path: { organizationId, venueId, stationId } });
      } catch (error) {
        if (problemCode(error) === PROBLEM.notFound) return null;
        throw error;
      }
    },
  });
}

/** A session's status, time left and plan — polled while the drawer is open so time left ticks down. */
export function useSession(organizationId: string, sessionId: string | undefined) {
  const api = useApi();
  return useQuery<Session>({
    queryKey: sessionKeys.one(organizationId, sessionId ?? ''),
    enabled: sessionId !== undefined,
    refetchInterval: 15_000,
    queryFn: () => api.getSessionStatus({ path: { organizationId, sessionId: sessionId as string } }),
  });
}

/** A session's timeline — its billed segments and its ordered event history (F-SES-21). */
export function useSessionTimeline(organizationId: string, sessionId: string | undefined) {
  const api = useApi();
  return useQuery<SessionTimeline>({
    queryKey: sessionKeys.timeline(organizationId, sessionId ?? ''),
    enabled: sessionId !== undefined,
    queryFn: () => api.getSessionTimeline({ path: { organizationId, sessionId: sessionId as string } }),
  });
}

/**
 * A session's ledger (F-SES-21 rule 6): its journal entries, newest first, a page at a time. A pass charges every
 * minute, so a long session's ledger is never read whole. The cursor is keyset, so charges posted while staff page
 * land before the first page and never shift the older ones.
 */
export function useSessionLedger(organizationId: string, sessionId: string | undefined) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: sessionKeys.ledger(organizationId, sessionId ?? ''),
    enabled: sessionId !== undefined,
    queryFn: ({ pageParam }): Promise<SessionLedgerPage> =>
      api.listSessionLedger({
        path: { organizationId, sessionId: sessionId as string },
        query: { limit: SESSION_LEDGER_PAGE_SIZE, ...(pageParam === undefined ? {} : { cursor: pageParam }) },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
  });
}

/** After a session action, re-read the session and its timeline (and the station's session lookup). */
function useSessionInvalidator(organizationId: string) {
  const queryClient = useQueryClient();
  return (sessionId: string) => {
    void queryClient.invalidateQueries({ queryKey: sessionKeys.one(organizationId, sessionId) });
    void queryClient.invalidateQueries({ queryKey: sessionKeys.timeline(organizationId, sessionId) });
    void queryClient.invalidateQueries({ queryKey: ['session', 'on-station'] });
  };
}

/** Grant free comp time to a session (F-SES-16). */
export function useCompSession(organizationId: string, sessionId: string) {
  const api = useApi();
  const invalidate = useSessionInvalidator(organizationId);
  return useMutation({
    mutationFn: (body: CompSession) => api.compSession({ path: { organizationId, sessionId }, headerParams: { 'idempotency-key': crypto.randomUUID() }, body }),
    onSuccess: () => invalidate(sessionId),
  });
}

/** Sell paid minutes onto a running session (F-SES-17). */
export function useSellSessionTime(organizationId: string, sessionId: string) {
  const api = useApi();
  const invalidate = useSessionInvalidator(organizationId);
  return useMutation({
    mutationFn: (body: SellSessionTime) => api.sellSessionTime({ path: { organizationId, sessionId }, body }),
    onSuccess: () => invalidate(sessionId),
  });
}

/** Message the player at the station (F-SES-20). Returns the timeline entry it recorded. */
export function useMessageSession(organizationId: string, sessionId: string) {
  const api = useApi();
  const invalidate = useSessionInvalidator(organizationId);
  return useMutation<SessionEvent, unknown, MessageSession>({
    mutationFn: (body: MessageSession) => api.messageSession({ path: { organizationId, sessionId }, body }),
    onSuccess: () => invalidate(sessionId),
  });
}

/** End a session, settling the final charge to the ledger (F-SES-14). */
export function useEndSession(organizationId: string, sessionId: string) {
  const api = useApi();
  const invalidate = useSessionInvalidator(organizationId);
  return useMutation({
    mutationFn: (body: EndSession) => api.endSession({ path: { organizationId, sessionId }, body }),
    onSuccess: () => invalidate(sessionId),
  });
}

/**
 * Start a session on a free station for a player (F-SES-04, spec/05 §3.1). The server funds it from the
 * player's passes first, then the comp or pay-as-you-go budget chosen here. On success the station's
 * session lookup and the floor snapshot are re-read, so the sheet and the tile turn to "in session".
 */
export function useStartSession(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Session, unknown, StartSession>({
    mutationFn: (body: StartSession) => api.startSession({ path: { organizationId, venueId }, body }),
    onSuccess: (_session, body) => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.onStation(organizationId, venueId, body.stationId) });
      void queryClient.invalidateQueries({ queryKey: floorKeys.snapshot(organizationId, venueId) });
    },
  });
}

/**
 * The stations a running session may move to (F-SES-18, spec/05 §7): another station of the venue that is
 * free and enabled — not the current one, not disabled, and not occupied. Sorted by station number so the
 * picker reads in floor order. Pure, so it is unit-tested; the server is the authority and refuses a
 * target that is not actually free (the move preview surfaces the rest).
 */
export function moveCandidates(stations: readonly FloorStationState[], currentStationId: string): FloorStationState[] {
  // A session stays on its own kind of station (ADR-0033): staff-run to staff-run, a PC with the app to another.
  const current = stations.find((station) => station.stationId === currentStationId);
  const staffRun = current === undefined ? undefined : isStaffRun(current);
  return stations
    .filter((station) => station.stationId !== currentStationId && station.status !== 'disabled' && !isOccupied(station))
    .filter((station) => staffRun === undefined || isStaffRun(station) === staffRun)
    .sort((a, b) => a.number - b.number);
}

/**
 * Move a running session to another station (F-SES-18, spec/05 §7). `confirm: false` **previews** the
 * cross-class warnings (passes may not apply, a higher age limit) without moving; `confirm: true` moves and
 * returns the moved session. A preview changes nothing, so only a real move re-reads the session, its
 * timeline and both stations' session lookups (the source freed, the target now busy).
 */
/**
 * Lock or unlock a session on a staff-run station (ADR-0033, `POST …/sessions/{id}/lock`): there is no kiosk to lock
 * from, so staff do it here. The session and the floor are re-read, so the tile and the clock follow.
 */
export function useLockSession(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { readonly sessionId: string; readonly stationId: string; readonly locked: boolean }) =>
      api.lockSession({ path: { organizationId, sessionId: input.sessionId }, body: { locked: input.locked } }),
    onSuccess: (session, input) => {
      queryClient.setQueryData(sessionKeys.onStation(organizationId, venueId, input.stationId), session);
      void queryClient.invalidateQueries({ queryKey: sessionKeys.one(organizationId, input.sessionId) });
      void queryClient.invalidateQueries({ queryKey: floorKeys.snapshot(organizationId, venueId) });
    },
  });
}

export function useMoveSession(organizationId: string, sessionId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<SessionMoveResult, unknown, MoveSession>({
    mutationFn: (body: MoveSession) => api.moveSession({ path: { organizationId, sessionId }, body }),
    onSuccess: (result) => {
      if (!result.moved) return;
      void queryClient.invalidateQueries({ queryKey: sessionKeys.one(organizationId, sessionId) });
      void queryClient.invalidateQueries({ queryKey: sessionKeys.timeline(organizationId, sessionId) });
      void queryClient.invalidateQueries({ queryKey: ['session', 'on-station'] });
      void queryClient.invalidateQueries({ queryKey: floorKeys.snapshot(organizationId, result.session.venueId) });
    },
  });
}

// -------------------------------------------------------------------------------------------------
// Console sessions (ADR-0034): booked blocks and controllers, one order at the end
// -------------------------------------------------------------------------------------------------
//
//   POST …/venues/{v}/sessions/quote          the start form's preview                  sessions:start
//   POST …/sessions/{id}/quote                "Sell time" / controllers preview         orders:create
//   POST …/sessions/{id}/add-time             book one more block                       orders:create
//   POST …/sessions/{id}/controllers          the controllers from now on               orders:create
//
// Every price comes from the server's one `quoteConsoleSession`: the console never prices a session itself.

/** The start form's price preview for a booking on a staff-run station; null input reads nothing. */
export function useConsoleQuote(organizationId: string, venueId: string, input: QuoteConsoleSession | null) {
  const api = useApi();
  return useQuery<ConsoleQuote>({
    queryKey: ['session', 'console-quote', organizationId, venueId, input],
    enabled: input !== null,
    staleTime: 15_000,
    placeholderData: (previous) => previous,
    queryFn: () => api.quoteConsoleSession({ path: { organizationId, venueId }, body: input as QuoteConsoleSession }),
  });
}

/** What adding a block or changing the controllers would do to what is due at the end; null input reads nothing. */
export function useSessionChangeQuote(organizationId: string, sessionId: string, input: QuoteSessionChange | null) {
  const api = useApi();
  return useQuery<SessionChangeQuote>({
    queryKey: ['session', 'change-quote', organizationId, sessionId, input],
    enabled: input !== null,
    staleTime: 15_000,
    placeholderData: (previous) => previous,
    queryFn: () => api.quoteSessionChange({ path: { organizationId, sessionId }, body: input as QuoteSessionChange }),
  });
}

/** Book one more block on a running staff-run session. */
export function useAddSessionTime(organizationId: string, sessionId: string) {
  const api = useApi();
  const invalidate = useSessionInvalidator(organizationId);
  return useMutation<Session, unknown, AddSessionTime>({
    mutationFn: (body) => api.addSessionTime({ path: { organizationId, sessionId }, body }),
    onSuccess: () => invalidate(sessionId),
  });
}

/** Change the controllers of a running staff-run session, from now on. */
export function useSetSessionControllers(organizationId: string, sessionId: string) {
  const api = useApi();
  const invalidate = useSessionInvalidator(organizationId);
  return useMutation<Session, unknown, SetSessionControllers>({
    mutationFn: (body) => api.setSessionControllers({ path: { organizationId, sessionId }, body }),
    onSuccess: () => invalidate(sessionId),
  });
}
